/**
 * Filters – Types, Constants, and Defaults
 */

import { DISTANCE_SLIDER } from '../../utils';
import type { FiltersJson } from '../../api';
import { DEFAULT_FILTERS_JSON } from '../../options';
import type { Option } from '../../options';
import type {
  FrequencyLevel,
  ExerciseLevel,
  InterestedInGender,
  RelationshipIntent,
  IntentEntry,
  WantsMoreKids,
  FlexibilityOption,
  EducationLevel,
  EmploymentStatus,
  PetType,
  Religion,
  Ethnicity,
  PoliticalView,
  IncomeRange,
  BodyType,
  SexualOrientation,
  SexualRole,
  SexualDynamics,
  ZodiacSign,
} from '../../options';

// Singular labels for the "Their Gender" filter (shared options use plural "Men/Women")
export const GENDER_FILTER_OPTIONS: Option[] = [
  { value: 'men', label: 'Man' },
  { value: 'women', label: 'Woman' },
  { value: 'nonbinary', label: 'Non-binary' },
];

// Filter state matching FiltersJson
export interface FiltersState {
  // Two-way matching toggle
  two_way_match: boolean;

  // Intelligence substrate filters
  show_human_users: boolean;
  show_ai_users: boolean;

  // Tier 1: Basic (always visible)
  age_min: number;
  age_max: number;
  distance_max_km: number;
  interested_in: InterestedInGender[];
  relationship_intents: RelationshipIntent[];
  recently_active_hours: number | null;

  // Tier 2: More Filters
  height_min_cm: number | null;
  height_max_cm: number | null;
  // Kids filters
  kids_no_kids: boolean;        // Checkbox: No kids
  kids_has_kids: boolean;       // Checkbox: Has kids
  kids_youngest_ages: string[]; // ['0-2', '3-5', '6-12', '13-17', '18+']
  kids_counts: number[];        // [1, 2, 3, 4, 5] (5 means 5+)
  kids_at_home: string | null;  // 'yes' | 'no' | null
  has_kids: string | null;      // Keep for backward compat
  wants_more_kids: WantsMoreKids[];
  smoking: FrequencyLevel[];
  drinking: FrequencyLevel[];
  cannabis: FrequencyLevel[];
  drugs: FrequencyLevel[];
  exercise_levels: ExerciseLevel[];
  preferred_structure: string | null;
  open_to_long_distance: FlexibilityOption[];
  open_to_relocation: FlexibilityOption[];
  zodiac_signs: ZodiacSign[];

  // Tier 3: Advanced Filters
  education_levels: EducationLevel[];
  employment_statuses: EmploymentStatus[];
  languages: string[];
  pets_ok_with: PetType[];
  religions: Religion[];

  // Tier 4: Demographics
  ethnicities: Ethnicity[];
  political_views: PoliticalView[];
  income_ranges: IncomeRange[];
  body_types: BodyType[];
  sexual_orientations: SexualOrientation[];

  // Tier 5: Sexual (gated)
  sexual_roles: SexualRole[];
  sexual_dynamics: SexualDynamics[];

  // Tier 6: Who They Are Open To
  inclusive_of_trans_nb: boolean;
  open_to_enm_poly: boolean;

  // Compound gender-intent entries
  intent_entries: IntentEntry[];

  // Activity filters
  activity_liked_me: boolean;
  activity_i_liked: boolean;
  activity_i_disliked: boolean;

  // Travel location override
  travel_city: string | null;
  travel_state: string | null;
  travel_country: string | null;
  travel_lat: number | null;
  travel_lng: number | null;
}

export const DEFAULT_FILTERS: FiltersState = {
  two_way_match: false,
  show_human_users: DEFAULT_FILTERS_JSON.show_human_users!,
  show_ai_users: DEFAULT_FILTERS_JSON.show_ai_users!,
  age_min: 18,
  age_max: 99,
  distance_max_km: DISTANCE_SLIDER.UNLIMITED,
  interested_in: [],
  relationship_intents: [],
  recently_active_hours: null,
  height_min_cm: null,
  height_max_cm: null,
  kids_no_kids: false,
  kids_has_kids: false,
  kids_youngest_ages: [],
  kids_counts: [],
  kids_at_home: null,
  has_kids: null,
  wants_more_kids: [],
  smoking: [],
  drinking: [],
  cannabis: [],
  drugs: [],
  exercise_levels: [],
  preferred_structure: null,
  open_to_long_distance: [],
  open_to_relocation: [],
  zodiac_signs: [],
  education_levels: [],
  employment_statuses: [],
  languages: [],
  pets_ok_with: [],
  religions: [],
  ethnicities: [],
  political_views: [],
  income_ranges: [],
  body_types: [],
  sexual_orientations: [],
  sexual_roles: [],
  sexual_dynamics: [],
  inclusive_of_trans_nb: false,
  open_to_enm_poly: false,
  intent_entries: [],
  activity_liked_me: false,
  activity_i_liked: false,
  activity_i_disliked: false,
  travel_city: null,
  travel_state: null,
  travel_country: null,
  travel_lat: null,
  travel_lng: null,
};

// Height filter slider constants
export const HEIGHT_RANGE = { MIN_CM: 137, MAX_CM: 213 };
export const HEIGHT_MIN_INCHES = 54;  // 4'6"
export const HEIGHT_MAX_INCHES = 84;  // 7'0"

// Recently active slider positions (index -> hours)
export const RECENTLY_ACTIVE_SLIDER = {
  MIN: 0,
  MAX: 6,
  POSITIONS: [24, 72, 168, 720, 4380, 8760, null] as (number | null)[],
  LABELS: ['Today', '3 days', '7 days', '30 days', '6 months', '1 year', 'Any'],
};

// ============================================================================
// Compile-time key parity check: FiltersState <-> FiltersJson
// If someone adds a field to FiltersJson but not FiltersState (or vice versa), tsc fails.
// ============================================================================
type FiltersJsonFlatKeys = Exclude<keyof FiltersJson,
  'eligibility' | 'intent' | 'availability' | 'availability_slots' | 'app'>;
type FiltersStateFilterKeys = Exclude<keyof FiltersState,
  'kids_no_kids' | 'kids_has_kids'>;

type _Missing = Exclude<FiltersJsonFlatKeys, FiltersStateFilterKeys>;
type _Extra = Exclude<FiltersStateFilterKeys, FiltersJsonFlatKeys>;
const _checkMissing: _Missing extends never ? true : _Missing = true;
const _checkExtra: _Extra extends never ? true : _Extra = true;
